import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import {
  Compass, Heart, LogOut, User, Shield, Crown,
  Image as ImageIcon, Menu, X, Map, Scale, MessageCircle, Info, CheckSquare, Mountain, Fish,
} from "lucide-react";
import NotificationBell from "./NotificationBell";
import MessagesBell from "./MessagesBell";
import ShareMenu from "./ShareMenu";
import LanguageSwitcher from "./LanguageSwitcher";
import LaunchCountdownBar from "./LaunchCountdownBar";
import SignupHeroBanner from "./SignupHeroBanner";
import FollowUsBar from "./FollowUsBar";
import TrialCountdownBar from "./TrialCountdownBar";
import PartnerShopsStrip from "./PartnerShopsStrip";

export default function Header({ active, compact = false }) {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [siteCount, setSiteCount] = useState(null);

  // Close drawer on route change
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Pull live campsite total once so the "Invite a friend" share text always
  // reflects the real DB size (preview & production differ).
  useEffect(() => {
    let active = true;
    import("../../lib/api").then(({ api }) => {
      api.get("/campsites/stats")
        .then((r) => { if (active && typeof r.data?.total === "number") setSiteCount(r.data.total); })
        .catch(() => {});
    });
    return () => { active = false; };
  }, []);

  // Lock body scroll when drawer open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [open]);

  const onLogout = async () => {
    setOpen(false);
    await logout();
    navigate("/");
  };

  const NAV_ITEMS = [
    { to: "/browse",    label: t("nav.browse"),    icon: Map,            id: "browse" },
    { to: "/wall",      label: t("nav.wall"),      icon: ImageIcon,      id: "wall" },
    { to: "/compare",   label: t("nav.compare"),   icon: Scale,          id: "compare" },
    { to: "/community", label: t("nav.community"), icon: MessageCircle,  id: "community" },
    { to: "/checklist", label: "Checklist",         icon: CheckSquare,    id: "checklist" },
    { to: "/fisheries", label: "Fisheries",          icon: Fish,           id: "fisheries" },
    { to: "/charities", label: "Charities",          icon: Mountain,       id: "charities" },
    { to: "/about",     label: t("nav.about"),     icon: Info,           id: "about" },
    { to: "/pro",       label: t("nav.pro"),       icon: Crown,          id: "pro" },
  ];

  return (
    <>
      {/* iter143 — When Landing renders <CondensedTopStrip /> above the Header
          it already carries the social icons and sign-up pill. Passing
          compact=true skips the two duplicated ribbons (Follow Us + big
          Sign Up Free banner) so the Featured Reel can sit higher up. We
          keep the "just joined the camping community" marquee visible on
          every page because it's the app's social-proof heartbeat and
          operators specifically flagged its disappearance as a bug. */}
      {!compact && <FollowUsBar />}
      {!compact && <SignupHeroBanner />}
      <LaunchCountdownBar />
      <TrialCountdownBar />
      {/* iter171 — Affiliate partner shops chip row rendered on every page
          (Landing already skips its local copy since this component now
          owns the strip). Silent when there are no enabled partners. */}
      <PartnerShopsStrip />
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b hairline" data-testid="site-header">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center justify-between gap-3">
        {/* BRAND */}
        <Link to="/" className="flex items-center gap-2 flex-shrink-0" data-testid="brand-link">
          <Compass className="w-5 h-5 text-[#ff4500]" strokeWidth={1.75} />
          <span className="font-display text-xl tracking-tight whitespace-nowrap">
            Camp<span className="text-[#ff4500]">Compass</span>
          </span>
          <span className="eyebrow hidden lg:inline ml-1">{t("nav.ukEdition")}</span>
        </Link>

        {/* DESKTOP NAV — only on real desktop widths (≥1024px) so phones in
            landscape and tablets still get the hamburger drawer. */}
        <nav className="hidden lg:flex items-center gap-6 lg:gap-8 text-sm">
          {NAV_ITEMS.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.id}`}
              className={`link-underline whitespace-nowrap flex items-center gap-1.5 ${active === n.id ? "text-[#ff4500]" : ""}`}
            >
              {n.id === "wall" || n.id === "pro" ? <n.icon className="w-4 h-4" strokeWidth={1.5} /> : null}
              {n.label}
            </Link>
          ))}
          {user && (
            <Link
              to="/favorites"
              data-testid="nav-favorites"
              className={`link-underline flex items-center gap-1.5 whitespace-nowrap ${active === "favorites" ? "text-[#ff4500]" : ""}`}
            >
              <Heart className="w-4 h-4" strokeWidth={1.5} /> {t("nav.favorites")}
            </Link>
          )}
        </nav>

        {/* RIGHT-SIDE CONTROLS */}
        <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
          <LanguageSwitcher testIdPrefix="header-lang" />
          {user ? (
            <>
              {user.is_pro ? (
                <span className="hidden lg:inline-flex items-center gap-1 text-[11px] mono uppercase tracking-wider px-2 py-1 bg-[#ff4500] text-white" data-testid="pro-badge">
                  <Crown className="w-3 h-3" strokeWidth={2} /> {t("nav.pro")}
                </span>
              ) : (
                <Link to="/pro" className="hidden lg:inline-flex items-center gap-1 text-[11px] mono uppercase tracking-wider px-2 py-1 border hairline hover:bg-[#ff4500] hover:text-white hover:border-[#ff4500]" data-testid="go-pro-header">
                  <Crown className="w-3 h-3" strokeWidth={2} /> {t("nav.goPro")}
                </Link>
              )}
              {user.role === "admin" && (
                <Link
                  to="/admin"
                  className="inline-flex items-center gap-1 text-[11px] mono uppercase tracking-wider px-2 py-1 bg-black text-white hover:bg-[#ff4500] transition-colors"
                  data-testid="nav-admin"
                  title={t("nav.admin_dashboard")}
                >
                  <Shield className="w-3 h-3" strokeWidth={2} />
                  <span className="hidden sm:inline">{t("nav.admin")}</span>
                </Link>
              )}
              <MessagesBell />
              <NotificationBell />
              <Link
                to="/profile"
                className="hidden sm:inline-flex items-center justify-center w-9 h-9 border hairline overflow-hidden hover:bg-[#fff4ee] hover:border-[#ff4500] transition-colors"
                data-testid="nav-profile"
                title={t("nav.profile")}
                aria-label={t("nav.profile")}
              >
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-full h-full object-cover" data-testid="nav-profile-avatar" />
                ) : (
                  <User className="w-4 h-4" strokeWidth={2} />
                )}
              </Link>
              <button onClick={onLogout} className="hidden sm:inline-flex btn-secondary !py-2 !px-3 text-xs items-center" data-testid="logout-btn">
                <LogOut className="w-3.5 h-3.5 mr-1.5" strokeWidth={2} /> {t("auth.logout")}
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="hidden sm:inline-block text-sm link-underline" data-testid="nav-login">{t("auth.login")}</Link>
              <Link to="/register" className="hidden sm:inline-block btn-primary !py-2 !px-4 text-xs" data-testid="nav-register">{t("auth.signUp")}</Link>
            </>
          )}

          {/* MOBILE HAMBURGER — visible up to 1024px so tablet & landscape phone get the drawer too */}
          <button
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 border hairline hover:bg-[#fff4ee]"
            aria-label={open ? t("nav.closeMenu") : t("nav.openMenu")}
            aria-expanded={open}
            data-testid="nav-toggle"
          >
            {open ? <X className="w-5 h-5" strokeWidth={2} /> : <Menu className="w-5 h-5" strokeWidth={2} />}
          </button>
        </div>
      </div>

      {/* MOBILE DRAWER */}
      {open && (
        <>
          {/* backdrop */}
          <div
            className="lg:hidden fixed inset-0 top-16 bg-black/40 z-30"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          {/* panel */}
          <nav
            className="lg:hidden absolute left-0 right-0 top-16 bg-white border-b hairline-strong shadow-[0_8px_24px_rgba(0,0,0,0.08)] max-h-[calc(100vh-64px)] overflow-y-auto z-40"
            data-testid="mobile-nav"
          >
            <ul className="py-2">
              {NAV_ITEMS.map((n) => (
                <li key={n.to}>
                  <Link
                    to={n.to}
                    className={`flex items-center gap-3 px-5 py-3.5 border-b hairline text-base font-display tracking-tight ${active === n.id ? "text-[#ff4500] bg-[#fff4ee]" : "hover:bg-[#fafafa]"}`}
                    data-testid={`mobile-nav-${n.id}`}
                  >
                    <n.icon className={`w-5 h-5 ${active === n.id ? "text-[#ff4500]" : "text-[#666]"}`} strokeWidth={1.75} />
                    {n.label}
                  </Link>
                </li>
              ))}
              {user && (
                <li>
                  <Link
                    to="/profile"
                    className={`flex items-center gap-3 px-5 py-3.5 border-b hairline text-base font-display tracking-tight ${active === "profile" ? "text-[#ff4500] bg-[#fff4ee]" : "hover:bg-[#fafafa]"}`}
                    data-testid="mobile-nav-profile"
                  >
                    <User className="w-5 h-5 text-[#666]" strokeWidth={1.75} /> {t("nav.profile")}
                  </Link>
                </li>
              )}
              {user && (
                <li>
                  <Link
                    to="/favorites"
                    className={`flex items-center gap-3 px-5 py-3.5 border-b hairline text-base font-display tracking-tight ${active === "favorites" ? "text-[#ff4500] bg-[#fff4ee]" : "hover:bg-[#fafafa]"}`}
                    data-testid="mobile-nav-favorites"
                  >
                    <Heart className="w-5 h-5 text-[#666]" strokeWidth={1.75} /> {t("nav.favorites")}
                  </Link>
                </li>
              )}
              {user?.role === "admin" && (
                <li>
                  <Link
                    to="/admin"
                    className="flex items-center gap-3 px-5 py-3.5 border-b hairline text-base font-display tracking-tight bg-black text-white hover:bg-[#ff4500]"
                    data-testid="mobile-nav-admin"
                  >
                    <Shield className="w-5 h-5" strokeWidth={1.75} /> {t("nav.admin_dashboard")}
                  </Link>
                </li>
              )}
              <li className="border-b hairline bg-[#fff4ee]" data-testid="mobile-invite-friend-li">
                {/* Modernised: previously mailto-only — now opens the full
                    social grid (WhatsApp, Instagram, Facebook, X, Threads,
                    Reddit, Pinterest, Telegram, LinkedIn, Messenger, Email,
                    Copy link). preferGrid skips the OS share sheet so users
                    see every social option explicitly. */}
                <ShareMenu
                  url={process.env.REACT_APP_PUBLIC_URL || (typeof window !== "undefined" ? window.location.origin : "")}
                  title="CampCompass UK · honest UK campsite comparisons"
                  text={`Found this app — the UK's largest campsite database${siteCount ? ` (${siteCount.toLocaleString()} sites)` : ""}. Real prices, honest reviews, no affiliate spin. Free to browse, £1.99/mo for Pro. Try it →`}
                  buttonClassName="w-full text-left flex items-center gap-3 px-5 py-3.5 text-base font-display tracking-tight text-[#ff4500] hover:bg-[#ffe2d0]"
                  buttonLabel={t("nav.inviteAFriend")}
                  preferGrid
                  testId="mobile-invite-friend"
                />
              </li>
            </ul>

            {/* Language switcher row */}
            <LanguageSwitcher variant="inline" testIdPrefix="mobile-lang" />

            {/* Auth actions */}
            <div className="border-t hairline-strong px-5 py-4 space-y-3 bg-[#fafafa]">
              {user ? (
                <>
                  <div className="flex items-center gap-2 text-sm">
                    <User className="w-4 h-4 text-[#666]" strokeWidth={1.5} />
                    <span className="font-mono text-xs truncate">{user.email}</span>
                    {user.is_pro && (
                      <span className="ml-auto text-[10px] mono uppercase tracking-wider px-1.5 py-0.5 bg-[#ff4500] text-white">{t("nav.pro")}</span>
                    )}
                  </div>
                  {!user.is_pro && (
                    <Link to="/pro" className="btn-primary text-xs w-full inline-flex items-center justify-center gap-2" data-testid="mobile-go-pro">
                      <Crown className="w-3.5 h-3.5" strokeWidth={2} /> {t("nav.goProPrice")}
                    </Link>
                  )}
                  <button onClick={onLogout} className="btn-secondary text-xs w-full inline-flex items-center justify-center gap-2" data-testid="mobile-logout">
                    <LogOut className="w-3.5 h-3.5" strokeWidth={2} /> {t("auth.logOut")}
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="btn-secondary text-xs w-full inline-flex items-center justify-center" data-testid="mobile-nav-login">
                    {t("auth.logIn")}
                  </Link>
                  <Link to="/register" className="btn-primary text-xs w-full inline-flex items-center justify-center" data-testid="mobile-nav-register">
                    {t("auth.createAccount")}
                  </Link>
                </>
              )}
            </div>
          </nav>
        </>
      )}
    </header>
    </>
  );
}
